<template>
    <div>
        <page :full-scroll="false">
            <div class="pt-3 mb-2">
                <router-link :to="{name: 'admin-ssnvs'}" class="text-muted">
                    <i class="fal fa-arrow-left"></i> Back to batches
                </router-link>
            </div>

            <form-errors :errors="formErrors" />

            <div v-if="batch">
                <h3>SSNVS Batch #{{ batch.id }}</h3>
                <p class="text-muted mb-1">
                    {{ batch.clients.join(', ') }} &middot;
                    {{ batch.date_start }} &ndash; {{ batch.date_end }} ({{ batch.date_basis_display }}) &middot;
                    created {{ batch.created_on.slice(0, 10) }} by {{ batch.created_by }}
                </p>
                <p>
                    <span class="badge" :class="awaitingResults ? 'badge-warning' : 'badge-success'">{{ batch.status_display }}</span>
                    <span v-if="batch.confirmation_number" class="ml-2 text-muted">SSA confirmation: {{ batch.confirmation_number }}</span>
                    <span class="ml-2 text-muted" v-if="batch.num_verified !== null">
                        ({{ batch.num_verified }} verified, {{ batch.num_resolved }} resolved, {{ batch.num_problems }} problems)
                    </span>
                </p>
                <p class="text-muted small" v-if="batch.num_verified !== null">
                    <i class="fal fa-info-circle"></i>
                    Marking a problem resolved excludes that employee from future batches. Un-resolving makes them eligible again.
                </p>

                <div class="mb-3">
                    <button class="btn btn-outline-primary" @click.prevent="onDownload">
                        <i class="fal fa-download"></i> Download Submission File
                    </button>
                    <label class="btn btn-outline-primary mb-0 ml-2">
                        <i class="fal fa-upload"></i> Upload SSA Result File
                        <input type="file" accept=".txt" hidden @change="onUploadResults">
                    </label>
                    <button v-if="awaitingResults" class="btn btn-outline-danger ml-2" @click.prevent="onDelete">
                        Delete Batch
                    </button>
                </div>

                <div class="alert alert-warning" v-if="uploadWarnings.length">
                    <b>Upload warnings:</b>
                    <ul class="mb-0">
                        <li v-for="(w, i) in uploadWarnings" :key="i">{{ w }}</li>
                    </ul>
                </div>

                <details class="mb-3" v-if="skipped.length">
                    <summary>{{ skipped.length }} employees skipped at generation</summary>
                    <ul>
                        <li v-for="s in skipped" :key="s.employee_id">
                            {{ s.client_code }}/{{ s.company_code }} &mdash; {{ s.name }} ({{ s.employee_number }}): {{ s.reason }}
                        </li>
                    </ul>
                </details>

                <fast-table
                    :key="tableKey"
                    :sections="sections"
                    :headers="headers"
                    :data="tableData"
                    :default-sort-stack="defaultSortStack"
                    :show-more-actions="false"
                >
                    <template v-slot:table-actions>
                        <div class="btn-group mr-2" role="group">
                            <button type="button" class="btn btn-sm" :class="groupMode === 'status' ? 'btn-primary' : 'btn-outline-primary'" @click="groupMode = 'status'">Group by status</button>
                            <button type="button" class="btn btn-sm" :class="groupMode === 'client' ? 'btn-primary' : 'btn-outline-primary'" @click="groupMode = 'client'">Group by client</button>
                        </div>
                        <span class="mr-2">
                            <label v-for="s in statusChoices" :key="s.key" class="mr-2 mb-0 small font-weight-normal">
                                <input type="checkbox" :checked="visibleStatuses.includes(s.key)" @change="toggleStatus(s.key)">
                                {{ s.title }} ({{ s.count }})
                            </label>
                        </span>
                        <button type="button" class="btn btn-sm btn-ssnvs-export" @click.prevent="onExportProblems">
                            <i class="far fa-fw fa-file-excel"></i> Export problems
                        </button>
                    </template>
                </fast-table>
            </div>
        </page>
    </div>
</template>

<script>
import api from '@/api'
import Page from '@/components/Page'
import FastTable from '@/components/FastTable'
import {
    maskSsn, formatDob, statusOptions, groupResults, escapeHtml,
} from './batchDetailTable'

export default {
    components: {Page, FastTable},
    data() {
        return {
            batch: null,
            results: [],
            skipped: [],
            uploadWarnings: [],
            formErrors: [],
            groupMode: 'status',
            visibleStatuses: [],
            revealedSsns: {},
            headers: [
                {label: 'Employee', id: 'employee', isSearchable: true},
                {label: 'Client', id: 'client', isSearchable: true, isFilterable: true, sortDataType: 'numeric'},
                {label: 'Company', id: 'company', isSearchable: true, isFilterable: true, sortDataType: 'numeric'},
                {label: 'SSN', id: 'ssn'},
                {label: 'DOB', id: 'dob'},
                {label: 'Result', id: 'result', sortDataType: 'numeric'},
                {label: 'Resolved', id: 'resolved'},
            ],
            defaultSortStack: [
                {id: 'client', isDesc: false},
                {id: 'company', isDesc: false},
                {id: 'result', isDesc: false},
                {id: 'employee', isDesc: false},
            ],
        }
    },
    computed: {
        batchId() {
            return this.$route.params.batchId
        },
        awaitingResults() {
            return this.batch && this.batch.status == 10
        },
        statusChoices() {
            return statusOptions(this.results)
        },
        groupedSections() {
            return groupResults(this.results, this.groupMode, this.visibleStatuses)
        },
        sections() {
            // FastTable's Row renders the per-section count itself, so the
            // title must not include it (else it shows up twice).
            return this.groupedSections.map(s => ({
                id: s.id,
                title: s.title,
                defaultIsClosed: false,
            }))
        },
        tableData() {
            return this.groupedSections.map(s => s.results.map(r => this.makeRow(r)))
        },
        tableKey() {
            // remount FastTable when the grouping dimension changes so its
            // once-in-data() closedSections/header state reinitializes cleanly.
            // Status toggles only filter rows, so they don't need a remount.
            return this.groupMode
        },
    },
    methods: {
        load() {
            api.get(`/admin/ssnvs/batches/${this.batchId}`).then(resp => {
                this.batch = resp.batch
                this.results = resp.results
                this.skipped = resp.skipped
                this.uploadWarnings = resp.upload_warnings
                this.revealedSsns = {}
                // show every status except Verified, which is hidden by default
                this.visibleStatuses = statusOptions(resp.results)
                    .map(s => s.key)
                    .filter(key => key !== 'verified')
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
        makeRow(r) {
            const problemUnresolved = r.is_problem && !r.resolved
            return {
                id: r.id,
                object: r,
                cells: [
                    `${r.employee_name} (${r.employee_number})`,
                    r.client_name ? `${r.client_code} — ${r.client_name}` : r.client_code,
                    r.company_name ? `${r.company_code} — ${r.company_name}` : r.company_code,
                    {
                        value: r.ssn_last_4,
                        html: this.ssnHtml(r),
                        callback: () => this.onReveal(r),
                        _meta: {type: 'action', additionalCSSClasses: 'ssnvs-ssn-cell'},
                    },
                    formatDob(r.dob),
                    {
                        value: r.result_rank,
                        html: this.resultHtml(r),
                        callback: () => {},
                        _meta: {
                            type: 'action',
                            additionalCSSClasses: problemUnresolved ? 'ssnvs-result-cell text-danger' : 'ssnvs-result-cell',
                        },
                    },
                    {
                        value: r.resolved ? 1 : 0,
                        html: this.resolvedHtml(r),
                        callback: () => this.onToggleResolved(r),
                        _meta: {type: 'action', additionalCSSClasses: 'ssnvs-resolved-cell'},
                    },
                ],
            }
        },
        ssnHtml(r) {
            const revealed = this.revealedSsns[r.id]
            if (revealed) {
                return `${escapeHtml(revealed)} <i class="fal fa-eye-slash"></i>`
            }
            return `${maskSsn(escapeHtml(r.ssn_last_4))} <i class="fal fa-eye"></i>`
        },
        resultHtml(r) {
            if (!r.has_result) {
                return '<span class="text-muted">Pending</span>'
            }
            let h = escapeHtml(r.code_description || '')
            if (r.death_indicator === 'Y') {
                h += ' <span class="badge badge-danger">Deceased per SSA</span>'
            }
            if (r.warning) {
                h += `<div class="small text-warning">${escapeHtml(r.warning)}</div>`
            }
            return h
        },
        resolvedHtml(r) {
            if (!r.is_problem) {
                return ''
            }
            if (r.resolved) {
                const who = r.resolved_by ? ` — ${escapeHtml(r.resolved_by)}` : ''
                return `<i class="fas fa-check text-success"></i> <span class="small text-muted">${escapeHtml(r.resolved_note)}${who}</span>`
            }
            return '<span class="text-muted small">Mark resolved</span>'
        },
        onReveal(r) {
            if (this.revealedSsns[r.id]) {
                const next = {...this.revealedSsns}
                delete next[r.id]
                this.revealedSsns = next
                return
            }
            api.get(`/admin/ssnvs/results/${r.id}/ssn`).then(resp => {
                this.revealedSsns = {...this.revealedSsns, [r.id]: resp.ssn}
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
        onToggleResolved(r) {
            const resolved = !r.resolved
            let note = r.resolved_note
            if (resolved) {
                note = window.prompt(
                    'Mark resolved? This employee won\'t be re-submitted in future batches.\n\nResolution note (optional):',
                    note || '',
                )
                if (note === null) {
                    return
                }
            }
            api.post(`/admin/ssnvs/results/${r.id}/resolve`, {resolved, note}).then(updated => {
                this.results = this.results.map(x => x.id === r.id ? {...x, ...updated} : x)
            }).catch(errors => {
                this.formErrors = errors.__all__
                this.load()
            })
        },
        toggleStatus(key) {
            if (this.visibleStatuses.includes(key)) {
                this.visibleStatuses = this.visibleStatuses.filter(k => k !== key)
            } else {
                this.visibleStatuses = [...this.visibleStatuses, key]
            }
        },
        onExportProblems() {
            // exports every unresolved problem in the batch (SSNs masked)
            api.redirect(`/admin/ssnvs/batches/${this.batchId}/export-problems`)
        },
        onDownload() {
            api.redirect(`/admin/ssnvs/batches/${this.batchId}/download`)
        },
        onUploadResults(evt) {
            const file = evt.target.files[0]
            if (!file) {
                return
            }
            this.formErrors = []
            this.$store.dispatch('START_LOADING')
            api.upload(`/admin/ssnvs/batches/${this.batchId}/results`, file, {fieldName: 'file'}).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Results uploaded: ${resp.num_verified} verified, ${resp.num_failed} problems.`)
                this.load()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__
            })
            evt.target.value = ''
        },
        onDelete() {
            if (!window.confirm('Delete this batch? Its employees will become eligible for future batches again.')) {
                return
            }
            api.del(`/admin/ssnvs/batches/${this.batchId}`).then(() => {
                this.$router.push({name: 'admin-ssnvs'})
            }).catch(errors => {
                this.formErrors = errors.__all__
            })
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'SSNVS Batch')
        this.load()
    },
}
</script>

<style>
/* Applied to the .cell-content wrapper in FastTable's Cell.vue (child component),
   so these rules are intentionally global, scoped by the ssnvs- class prefix. */
.ssnvs-ssn-cell a,
.ssnvs-resolved-cell a,
.ssnvs-result-cell a {
    color: inherit;
    text-decoration: none;
}
.ssnvs-ssn-cell a,
.ssnvs-resolved-cell a {
    cursor: pointer;
}
.ssnvs-result-cell a {
    cursor: default;
}

/* solid orange (primary) button - $brand-orange from _custom-colors.scss.
   Double-class selector so it beats Bootstrap's single-class .btn variants
   regardless of stylesheet order. */
.btn.btn-ssnvs-export {
    color: #fff;
    background-color: #FE4E00;
    border-color: #FE4E00;
}
.btn.btn-ssnvs-export:hover,
.btn.btn-ssnvs-export:focus {
    color: #fff;
    background-color: #D44100;
    border-color: #D44100;
}
</style>
